import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import DownIcon from '../../../../img/ant-down-icon.svg';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import CollapsibleTags from './CollapsibleTags';

/**
 * Normalizes a value to number for comparison (handles string "1" -> 1)
 * @param {string|number} val - Value to normalize
 * @returns {number} Normalized numeric value
 */
const normalizeValue = (val) => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseInt(val, 10);
  return val;
};


/**
 * Range select component for risk level filtering matching ElasticsearchSelect style exactly
 */
const RangeSelect = ({
  label,
  value = [],
  onChange,
  placeholder,
  allowClear = true,
  mode = 'multiple',
  maxTagCount = 1,
  style = { width: '100%' },
  showSelectedTags = true,
  ...props
}) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localValue, setLocalValue] = useState([]);

  const riskLevels = [
    { value: 1, label: t('legends.risk.low') },
    { value: 2, label: t('legends.risk.light') },
    { value: 3, label: t('legends.risk.moderate') },
    { value: 4, label: t('legends.risk.high') },
    {
      value: 5,
      label: t('legends.risk.extremelyHigh'),
    },
  ];

  // Normalize incoming values to numbers for proper comparison
  const normalizedValue = Array.isArray(value)
    ? value.map(normalizeValue).filter((v) => !isNaN(v))
    : [];

  const handleChange = (newValue) => {
    if (onChange) {
      const normalizedNewValue = Array.isArray(newValue)
        ? newValue.map(normalizeValue)
        : [];
      const selectedItems = normalizedNewValue.map((val) => {
        const option = riskLevels.find((opt) => opt.value === val);
        return option
          ? { value: val, label: option.label }
          : { value: val, label: String(val) };
      });

      onChange(normalizedNewValue, selectedItems);
    }
  };

  const handleRemoveTag = (valueToRemove) => {
    const normalizedRemove = normalizeValue(valueToRemove);
    const newValue = normalizedValue.filter((v) => v !== normalizedRemove);
    const remainingOptions = riskLevels.filter((level) =>
      newValue.includes(level.value)
    );
    const transformedItems = remainingOptions.map((item) => ({
      label: item.label,
      value: item.value,
    }));

    if (onChange) {
      onChange(newValue, transformedItems);
    }
  };

  const selectOptions = riskLevels.map((level) => ({
    value: level.value,
    label: level.label,
  }));

  // Mobile: Sync localValue with value when sheet opens
  useEffect(() => {
    if (sheetOpen) {
      setLocalValue(normalizedValue);
    }
  }, [sheetOpen]);

  // Mobile: Toggle selection
  const handleMobileSelect = useCallback((optionValue) => {
    setLocalValue((prev) => {
      if (prev.includes(optionValue)) {
        return prev.filter((v) => v !== optionValue);
      }
      return [...prev, optionValue];
    });
  }, []);

  // Mobile: Apply selection and close
  const handleMobileDone = useCallback(() => {
    const selectedItems = localValue.map((val) => {
      const option = riskLevels.find((opt) => opt.value === val);
      return option
        ? { value: val, label: option.label }
        : { value: val, label: String(val) };
    });
    if (onChange) {
      onChange(localValue, selectedItems);
    }
    setSheetOpen(false);
  }, [localValue, riskLevels, onChange]);

  // Mobile: Get display text
  const mobileDisplayValue = useMemo(() => {
    if (normalizedValue.length === 0) return '';
    if (normalizedValue.length === 1) {
      const option = riskLevels.find((opt) => opt.value === normalizedValue[0]);
      return option?.label || normalizedValue[0];
    }
    return t('{{count}} sélectionné(s)', { count: normalizedValue.length });
  }, [normalizedValue, riskLevels, t]);

  const selectedCount = localValue.length;

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-gray-700">{t(label)}</label>
        </div>

        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || placeholder || t('Select an option')}
          </span>
          <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={selectedCount > 0 ? `${t(label)} (${selectedCount})` : t(label)}
          showDone={true}
          onDone={handleMobileDone}
          doneLabel={t('Terminé')}
          doneDisabled={false}
        >
          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="py-1">
              {riskLevels.map((option) => {
                const isSelected = localValue.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                    onClick={() => handleMobileSelect(option.value)}
                  >
                    <div
                      className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                        isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check size={10} color="#fff" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-900">{option.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        <label className="select-label">{t(label)}</label>
      </div>

      <Select
        className="elasticsearch-select"
        mode={mode}
        value={normalizedValue}
        onChange={handleChange}
        placeholder={placeholder || t('Select an option')}
        allowClear={allowClear}
        style={style}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={(omittedValues) =>
          `+${omittedValues.length} ${omittedValues.length > 1 ? 'sélections' : 'sélection'}`
        }
        showSearch={false}
        options={selectOptions}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        {...props}
      />

      {showSelectedTags && (
        <CollapsibleTags
          value={normalizedValue}
          getLabel={(val) => {
            const option = riskLevels.find((opt) => opt.value === normalizeValue(val));
            return option ? option.label : val;
          }}
          onRemove={handleRemoveTag}
        />
      )}
    </div>
  );
};

export default React.memo(RangeSelect);
